import React from 'react';
import { MapIcon } from '@heroicons/react/24/outline';
import UnifiedMap from './UnifiedMap';
import { useListMapView } from '../hooks/useListMapView';

const BOTTOM_NAV_HEIGHT = 60;
const FLOATING_BUTTON_OFFSET = BOTTOM_NAV_HEIGHT + 16;

/**
 * Reusable List/Map View component with mobile sheet gestures
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.listContent - Content to render in the list pane
 * @param {Array} props.mapPins - Array of pins to display on the map (hotspots format)
 * @param {Array} props.projects - Array of projects to display on the map (for UnifiedMap projects prop)
 * @param {Function} props.onMapPinSelect - Callback when a map pin is selected
 * @param {Function} props.onProjectSelect - Callback when a project is selected (for projects prop)
 * @param {Function} props.onMarkerSelect - Callback when a marker is selected (for projects prop)
 * @param {Function} props.renderMapPreview - Function to render preview card for selected item
 * @param {Function} props.renderMapToggleButton - Optional custom render for map toggle button
 * @param {Function} props.renderFloatingActions - Optional floating action buttons
 * @param {Object} props.mapConfig - Map configuration
 * @param {string} props.mapConfig.mode - Map mode ('view' or 'edit')
 * @param {string} props.mapConfig.layoutContext - Layout context ('explore', etc.)
 * @param {Array} props.mapConfig.bounds - Map bounds [[southLat, westLng], [northLat, eastLng]]
 * @param {boolean} props.mapConfig.autoCenterOnUser - Whether to auto-center on user location
 * @param {Object} props.hookConfig - Configuration for useListMapView hook
 * @param {boolean} props.enableMap - Whether map functionality is enabled
 * @param {string} props.emptyStateTitle - Title for empty state
 * @param {string} props.emptyStateMessage - Message for empty state
 * @param {React.ReactNode} props.emptyStateIcon - Icon for empty state
 * @param {Function} props.onViewportStateChange - Callback when viewport state changes, receives { mapViewportBbox, mapViewportCenter, mapViewportRadius, mapUserInteracted, mapSyncEnabled }
 * @param {Object} props.hookState - Optional: Pass result from useListMapView hook to share state with parent. If not provided, a local instance is created.
 */
const ListMapView = ({
  listContent,
  mapPins = [],
  projects = null, // For UnifiedMap projects prop (alternative to mapPins)
  onMapPinSelect,
  onProjectSelect = null,
  onMarkerSelect = null,
  renderMapPreview,
  renderMapToggleButton,
  renderFloatingActions,
  mapConfig = {},
  hookConfig = {},
  enableMap = true,
  emptyStateTitle = 'No results found',
  emptyStateMessage = 'Try adjusting your search or filters',
  emptyStateIcon,
  onViewportStateChange,
  hookState,
}) => {
  // Always call the hook to follow Rules of Hooks, but we'll prefer props.hookState if available.
  // This allows the component to be used standalone or with lifted state.
  const localHookState = useListMapView({
    enableMap,
    ...hookConfig,
  });

  // Use provided state if available, otherwise local
  const effectiveState = hookState || localHookState;

  const {
    isMobile,
    viewMode,
    mobileSheetState,
    mobileSheetHeight,
    mobileViewportHeight,
    isFullyExpanded, // Replaces hardcoded checks
    userLocation,
    mapViewportBbox,
    mapViewportCenter,
    mapViewportRadius,
    mapUserInteracted,
    mapSyncEnabled,
    hasMounted,
    sheetAnimationsEnabled,
    selectedItem,
    setSelectedItem,
    shouldRenderMobileMap,
    sheetHeightValue,
    sheetTopValue,
    sheetTransitionClass,
    sheetOpacityTransition,
    handleMobileViewChange,
    handleMobileViewToggle,
    handleSheetPointerDown,
    handleMapViewportChange,
    handleClearMapAreaFilter,
    handleEnableMapAreaFilter,
  } = effectiveState;

  const MOBILE_SHEET_MAP = 10;
  const mapPaneRef = React.useRef(null);
  
  // Track if sheet is transitioning to delay map hiding
  const [isSheetExpanding, setIsSheetExpanding] = React.useState(false);
  const sheetExpandingTimeoutRef = React.useRef(null);
  
  // Track if map has ever been mounted - once mounted, keep it mounted (but hidden) for performance
  const hasMapBeenMountedRef = React.useRef(false);
  
  // Monitor sheet height changes to detect expansion
  React.useEffect(() => {
    if (isFullyExpanded && shouldRenderMobileMap) {
      // Sheet is expanding from map view to list view
      setIsSheetExpanding(true);
      // Clear any existing timeout
      if (sheetExpandingTimeoutRef.current) {
        clearTimeout(sheetExpandingTimeoutRef.current);
      }
      // Delay hiding map until after animation (300ms matches transition duration)
      sheetExpandingTimeoutRef.current = setTimeout(() => {
        setIsSheetExpanding(false);
      }, 350); // Slightly longer than transition to ensure smooth fade
    } else if (!isFullyExpanded) {
      // Sheet is collapsing, show map immediately
      setIsSheetExpanding(false);
      if (sheetExpandingTimeoutRef.current) {
        clearTimeout(sheetExpandingTimeoutRef.current);
      }
    }
    
    return () => {
      if (sheetExpandingTimeoutRef.current) {
        clearTimeout(sheetExpandingTimeoutRef.current);
      }
    };
  }, [mobileSheetHeight, shouldRenderMobileMap, isFullyExpanded]);

  // Track when map is first mounted (for both desktop and mobile)
  // Only mark as mounted when user explicitly switches to map view, not during initial render
  React.useEffect(() => {
    const shouldMountMap = !isMobile 
      ? (enableMap && viewMode === 'map')
      : (enableMap && mobileSheetState === 'map' && (shouldRenderMobileMap || isSheetExpanding));
    
    if (shouldMountMap && !hasMapBeenMountedRef.current && hasMounted) {
      hasMapBeenMountedRef.current = true;
    }
  }, [isMobile, enableMap, viewMode, mobileSheetState, shouldRenderMobileMap, isSheetExpanding, hasMounted]);

  // Expose viewport state to parent via callback
  React.useEffect(() => {
    if (onViewportStateChange) {
      onViewportStateChange({
        mapViewportBbox,
        mapViewportCenter,
        mapViewportRadius,
        mapUserInteracted,
        mapSyncEnabled,
        handleClearMapAreaFilter,
        handleEnableMapAreaFilter,
      });
    }
  }, [mapViewportBbox, mapViewportCenter, mapViewportRadius, mapUserInteracted, mapSyncEnabled, onViewportStateChange, handleClearMapAreaFilter, handleEnableMapAreaFilter]);

  // Track initial bounds to prevent recalculation after user interaction
  const initialBoundsRef = React.useRef(null);
  const projectsHashRef = React.useRef('');
  
  // Calculate map bounds from pins or projects
  // Only recalculate if:
  // 1. Explicit bounds provided in config (always use those)
  // 2. Projects/pins changed AND user hasn't interacted yet
  // 3. Initial bounds haven't been set yet
  const mapBounds = React.useMemo(() => {
    // If explicit bounds provided, use them
    if (mapConfig.bounds) {
      initialBoundsRef.current = mapConfig.bounds;
      return mapConfig.bounds;
    }
    
    // If user has interacted, don't recalculate bounds (prevent snapping)
    if (mapUserInteracted && initialBoundsRef.current) {
      return initialBoundsRef.current;
    }
    
    // Calculate hash of project IDs to detect if projects actually changed
    const currentProjectsHash = projects 
      ? projects.map(p => p.id).sort().join(',')
      : '';
    const projectsChanged = currentProjectsHash !== projectsHashRef.current;
    
    // Try to get bounds from projects (if using projects prop)
    if (projects && projects.length > 0) {
      const validProjects = projects.filter((p) => {
        if (p.geolocation) {
          const pos = typeof p.geolocation === 'string' 
            ? JSON.parse(p.geolocation) 
            : p.geolocation;
          return pos && (Number.isFinite(pos.lat) || Number.isFinite(pos.latitude)) && 
                 (Number.isFinite(pos.lng) || Number.isFinite(pos.longitude));
        }
        return Number.isFinite(p.lat) && Number.isFinite(p.lng);
      });
      
      if (validProjects.length > 0) {
        const positions = validProjects.map(p => {
          if (p.geolocation) {
            const pos = typeof p.geolocation === 'string' ? JSON.parse(p.geolocation) : p.geolocation;
            return {
              lat: pos.lat || pos.latitude,
              lng: pos.lng || pos.longitude
            };
          }
          return { lat: p.lat, lng: p.lng };
        });
        
        const lats = positions.map(p => p.lat);
        const lngs = positions.map(p => p.lng);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        
        const latPadding = (maxLat - minLat) * 0.1 || 0.01;
        const lngPadding = (maxLng - minLng) * 0.1 || 0.01;
        
        const calculatedBounds = [
          [minLat - latPadding, minLng - lngPadding],
          [maxLat + latPadding, maxLng + lngPadding],
        ];
        
        // Only update initial bounds if projects actually changed or it's the first time
        if (projectsChanged || !initialBoundsRef.current) {
          initialBoundsRef.current = calculatedBounds;
          projectsHashRef.current = currentProjectsHash;
        }
        
        return initialBoundsRef.current;
      }
    }
    
    // Try to get bounds from mapPins
    if (mapPins && mapPins.length > 0) {
      const validPins = mapPins.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
      if (validPins.length > 0) {
        const lats = validPins.map(p => p.lat);
        const lngs = validPins.map(p => p.lng);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        
        const latPadding = (maxLat - minLat) * 0.1 || 0.01;
        const lngPadding = (maxLng - minLng) * 0.1 || 0.01;
        
        const calculatedBounds = [
          [minLat - latPadding, minLng - lngPadding],
          [maxLat + latPadding, maxLng + lngPadding],
        ];
        
        // Only update initial bounds if it's the first time
        if (!initialBoundsRef.current) {
          initialBoundsRef.current = calculatedBounds;
        }
        
        return initialBoundsRef.current;
      }
    }
    
    return initialBoundsRef.current || null;
  }, [mapPins, projects, mapConfig.bounds, mapUserInteracted]);

  // Default map toggle button
  const defaultMapToggleButton = (
    <button
      onClick={handleMobileViewToggle}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold shadow-xl active:scale-95 transition-transform"
    >
      <MapIcon className="w-4 h-4" />
      Map
    </button>
  );

  // Desktop layout
  if (!isMobile) {
    // On desktop, use grid layout only when map view is active (split view)
    // When list view is active, use flex column layout
    // Use viewMode directly to ensure it updates correctly
    const shouldShowMapPane = enableMap && viewMode === 'map';
    
    return (
      <div className="h-full flex flex-col bg-white dark:bg-gray-900 relative overflow-hidden" style={{ minHeight: '100dvh' }}>
        <div className="flex-1 overflow-hidden">
          <div className={`h-full ${shouldShowMapPane ? 'grid grid-cols-[minmax(0,_3fr)_minmax(360px,_2fr)] gap-0' : 'flex flex-col'}`}>
            {/* List Pane - Always show on desktop */}
            <div className={`w-full h-full overflow-hidden ${shouldShowMapPane ? 'border-r border-gray-200 dark:border-gray-800 min-h-0' : ''}`}>
              {listContent}
            </div>

            {/* Map Pane - Mount once when first needed, then keep mounted (but hidden) for performance */}
            {/* Only mount if user explicitly switched to map view OR if it's been mounted before */}
            {(shouldShowMapPane || hasMapBeenMountedRef.current) && (
              <div 
                ref={mapPaneRef} 
                className="w-full relative block h-full min-h-0" 
                style={{ 
                  height: '100%', 
                  minHeight: '100%',
                  display: shouldShowMapPane ? 'block' : 'none' // Hide but keep mounted
                }}
              >
                <div style={{ height: '100%', width: '100%' }}>
                  <UnifiedMap 
                    mode={mapConfig.mode || 'view'}
                    projects={projects || undefined}
                    hotspots={projects ? undefined : mapPins}
                    showHotspots={!projects}
                    showProjectNumbers={!!projects}
                    userLocation={userLocation}
                    layoutContext={mapConfig.layoutContext || 'explore'}
                    bounds={mapBounds}
                    autoCenterOnUser={mapConfig.autoCenterOnUser !== false && !mapBounds}
                    onHotspotSelect={projects ? undefined : (hotspot) => {
                      if (hotspot?._cluster) {
                        return;
                      }
                      const item = hotspot._item || hotspot;
                      setSelectedItem(item);
                      if (onMapPinSelect) {
                        onMapPinSelect(item, hotspot);
                      }
                    }}
                    onProjectSelect={projects ? (projectId) => {
                      const project = projects.find(p => p.id === projectId);
                      if (project) {
                        setSelectedItem(project);
                        if (onProjectSelect) {
                          onProjectSelect(projectId);
                        }
                      }
                    } : undefined}
                    onMarkerSelect={projects ? (project) => {
                      setSelectedItem(project);
                      if (onMarkerSelect) {
                        onMarkerSelect(project);
                      }
                    } : undefined}
                    selectedHotspotId={projects ? undefined : selectedItem?.id}
                    selectedProjectId={projects ? selectedItem?.id : undefined}
                    onViewportChange={handleMapViewportChange}
                  />
                </div>
                
                {/* Desktop preview card - use very high z-index to be above Leaflet map controls */}
                {selectedItem && renderMapPreview && (
                  <div 
                    className="absolute bottom-20 left-4 right-4 flex justify-center" 
                    style={{ 
                      pointerEvents: 'auto',
                      zIndex: 10000
                    }}
                  >
                    <div className="w-full max-w-sm">
                      {renderMapPreview(selectedItem, { onClose: () => setSelectedItem(null) })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Mobile layout
  return (
    <div 
      className="relative w-full bg-white dark:bg-gray-900" 
      style={{ 
        height: mobileViewportHeight ? `${mobileViewportHeight}px` : '100dvh',
        maxHeight: mobileViewportHeight ? `${mobileViewportHeight}px` : '-webkit-fill-available',
      }}
    >
      {/* Map Layer - Mount once when first needed, then keep mounted (but hidden) for performance */}
      {/* Only mount if: 
          1. Map has been mounted before (keep it mounted), OR
          2. User explicitly switched to map view (mobileSheetState === 'map' AND component has mounted) */}
      {enableMap && (
        hasMapBeenMountedRef.current || 
        (hasMounted && mobileSheetState === 'map' && shouldRenderMobileMap)
      ) && (
        <div 
          className="absolute inset-0 z-0"
          style={{
            // Show when visible, hide but keep mounted otherwise
            opacity: (shouldRenderMobileMap || isSheetExpanding) ? 1 : 0,
            pointerEvents: (shouldRenderMobileMap || isSheetExpanding) ? 'auto' : 'none',
            visibility: (shouldRenderMobileMap || isSheetExpanding) ? 'visible' : 'hidden',
            transition: hasMounted && sheetAnimationsEnabled ? 'opacity 0.3s ease-out, visibility 0.3s ease-out' : 'none',
            touchAction: (shouldRenderMobileMap || isSheetExpanding) ? 'auto' : 'none'
          }}
        >
          <UnifiedMap
            mode={mapConfig.mode || 'view'}
            projects={projects || undefined}
            hotspots={projects ? undefined : mapPins}
            showHotspots={!projects}
            showProjectNumbers={!!projects}
            userLocation={userLocation}
            layoutContext={mapConfig.layoutContext || 'explore'}
            bounds={mapBounds}
            autoCenterOnUser={mapConfig.autoCenterOnUser !== false && !mapBounds && shouldRenderMobileMap}
            ignoreUserInteraction={!shouldRenderMobileMap}
            onHotspotSelect={projects ? undefined : (hotspot) => {
              if (hotspot?._cluster) {
                return;
              }
              const item = hotspot._item || hotspot;
              setSelectedItem(item);
              if (onMapPinSelect) {
                onMapPinSelect(item, hotspot);
              }
            }}
            onProjectSelect={projects ? (projectId) => {
              const project = projects.find(p => p.id === projectId);
              if (project) {
                setSelectedItem(project);
                if (onProjectSelect) {
                  onProjectSelect(projectId);
                }
              }
            } : undefined}
            onMarkerSelect={projects ? (project) => {
              setSelectedItem(project);
              if (onMarkerSelect) {
                onMarkerSelect(project);
              }
            } : undefined}
            selectedHotspotId={projects ? undefined : selectedItem?.id}
            selectedProjectId={projects ? selectedItem?.id : undefined}
            onViewportChange={handleMapViewportChange}
          />
        </div>
      )}

      {/* Map Preview Card */}
      {selectedItem && enableMap && renderMapPreview && (
        <div 
          className="fixed left-3 right-3 z-[600]"
          style={{
            bottom: 'calc(var(--app-safe-area-bottom) + 16px)',
            animation: 'slideUp 0.25s ease-out',
          }}
        >
          {renderMapPreview(selectedItem, { onClose: () => setSelectedItem(null) })}
        </div>
      )}

      {/* Floating Actions */}
      {renderFloatingActions && isFullyExpanded && !selectedItem && (
        <div 
          className="fixed right-4 z-[1000]"
          style={{
            bottom: `calc(var(--app-safe-area-bottom-nav) + ${FLOATING_BUTTON_OFFSET}px + 8px)`,
          }}
        >
          {renderFloatingActions()}
        </div>
      )}

      {/* Map Toggle Button */}
      {isFullyExpanded && !selectedItem && (
        <div 
          className="fixed left-1/2 -translate-x-1/2 z-[1000]"
          style={{ bottom: `calc(var(--app-safe-area-bottom-nav) + ${FLOATING_BUTTON_OFFSET}px + 8px)` }}
        >
          {renderMapToggleButton ? renderMapToggleButton({ onToggle: handleMobileViewToggle }) : defaultMapToggleButton}
        </div>
      )}

      {/* Bottom Sheet */}
      <div 
        className={`absolute inset-x-0 bottom-0 z-[500] ${
          selectedItem ? 'opacity-0 pointer-events-none' : 'opacity-100 pointer-events-none'
        } ${
          !isFullyExpanded ? 'bg-white dark:bg-gray-900 rounded-t-[20px]' : ''
        }`}
        style={{ top: sheetTopValue, paddingBottom: 'var(--app-safe-area-bottom)' }}
      >
        <div 
          className={`${selectedItem ? 'pointer-events-none' : 'pointer-events-auto'} bg-white dark:bg-gray-900 flex flex-col relative ${
            sheetAnimationsEnabled ? sheetTransitionClass : ''
          } ${
            !isFullyExpanded ? 'rounded-t-[20px] shadow-[0_-4px_20px_rgba(0,0,0,0.15)]' : ''
          }`}
          style={{ height: sheetHeightValue }}
        >
          {/* Drag Handle */}
          {!isFullyExpanded && (
            <div
              className="flex-shrink-0 cursor-grab active:cursor-grabbing touch-none select-none"
              onPointerDown={handleSheetPointerDown}
              onClick={(e) => {
                if (!e.target.closest('button') && mobileSheetHeight <= MOBILE_SHEET_MAP + 5) {
                  handleMobileViewChange('list');
                }
              }}
            >
              <div className="flex justify-center pt-3 pb-2">
                <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
              </div>
                <div className="px-4 pb-3 text-center">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {(projects?.length || mapPins?.length || 0)} result{(projects?.length || mapPins?.length || 0) !== 1 ? 's' : ''}
                  </p>
                </div>
            </div>
          )}
          
          {/* List Content */}
          <div 
            className="flex-1 min-h-0 overflow-hidden"
            style={{
              opacity: mobileSheetHeight <= MOBILE_SHEET_MAP + 5 ? 0 : 1,
              transition: sheetOpacityTransition,
            }}
          >
            {listContent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListMapView;
